/**
 * QLTY029 (OtkCompanyCertRecordDetail) embedded ReactTabulator session helpers.
 * Reuses OTK company-cert state-list filter/sorter remaps; does not broaden qlty033.
 */
import {
  OTK_COMPANY_CERT_KIND,
  decorateOtkCertTreeRows,
  mapOtkCompanyCertHeaderFiltersToQuery,
  mapOtkCompanyCertSorterField,
} from './otkCompanyCertStateListSync';
import { cleanSorters, extractSafePaginationParams } from './tabulatorProgressiveLoading';

export const QLTY029_MASK_PAGE = 'OtkCompanyCertRecordDetail';
export const QLTY029_PAGED_URL = '/api/company_certificates/paged';
export const QLTY029_PRINT_URL = '/api/company_certificates/export/print';
export const QLTY029_XLSX_URL = '/api/company_certificates/export/xlsx';
export const QLTY029_SHOW_URL = (id) => `/api/company_certificates/${id}`;

export const QLTY029_DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  name: true,
  doc_status: true,
  description: true,
  custom_start_of_validity: true,
  custom_end_of_validity: true,
  cert_description: true,
  protocol: true,
  state: true,
  created_at: true,
  updated_at: true,
});

export const QLTY029_MASK_DIALOG_FIELDS = Object.freeze([
  'name',
  'doc_status',
  'description',
  'custom_start_of_validity',
  'custom_end_of_validity',
  'cert_description',
  'protocol',
  'state',
  'created_at',
  'updated_at',
]);

/** Map qlty029 Slovak doc_status labels onto BE certificate_status values. */
export function normalizeDocStatusFilterValue(val) {
  const v = String(val ?? '').trim();
  if (v === 'Aktívna' || v === 'Aktívny') return 'Aktívny';
  if (v === 'Deaktivovaná' || v === 'Deaktivovaný') return 'Deaktivovaný';
  return v;
}

/**
 * Header filters → server filter_* params for embedded company-scoped paged tree.
 * `doc_status` is remapped to `certificate_status` (same contract as custom_doc_status).
 */
export function mapQlty029HeaderFiltersToQuery(headerFilters) {
  const remapped = (Array.isArray(headerFilters) ? headerFilters : []).map((f) => {
    if (!f || f.field == null) return f;
    if (f.field === 'doc_status') {
      return {
        ...f,
        field: 'custom_doc_status',
        value: normalizeDocStatusFilterValue(f.value),
      };
    }
    return f;
  });
  return mapOtkCompanyCertHeaderFiltersToQuery(remapped);
}

export function mapQlty029SortersToQuery(sorters) {
  return cleanSorters(sorters)
    .map((s) => {
      const field = mapOtkCompanyCertSorterField(s?.field || s?.column);
      if (!field) return null;
      if (field === 'doc_status') return { field: 'certificate_status', dir: s?.dir === 'desc' ? 'desc' : 'asc' };
      return { field, dir: s?.dir === 'desc' ? 'desc' : 'asc' };
    })
    .filter(Boolean);
}

export function decorateQlty029PagedRows(rawRows) {
  const decorated = decorateOtkCertTreeRows(Array.isArray(rawRows) ? rawRows : []);
  return decorated.map((c) => {
    if (!c || typeof c !== 'object') return c;
    const docStatus = c.deactivation_date ? 'Deaktivovaná' : 'Aktívna';
    const children = Array.isArray(c._children)
      ? c._children.map((ch) => (ch && typeof ch === 'object'
        ? {
            ...ch,
            doc_status: docStatus,
            cert_description: c.description,
          }
        : ch))
      : [];
    return {
      ...c,
      doc_status: docStatus,
      cert_description: c.description,
      _children: children,
    };
  });
}

/**
 * Build paged query extras for the embedded qlty029 table.
 * @param {object} opts
 * @param {object} [opts.paramsFromTabulator]
 * @param {number} opts.pageSize
 * @param {string|number|null} opts.companyId
 * @param {Function} [opts.getTable]
 * @param {string} [opts.toolbarSearch]
 */
export function buildQlty029RequestParams({
  paramsFromTabulator = {},
  pageSize,
  companyId,
  getTable,
  toolbarSearch = '',
} = {}) {
  const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
  if (extra.size == null) extra.size = pageSize;
  extra.type = 'otk';
  if (companyId != null && companyId !== '') extra.company_id = companyId;

  const rawSorters = paramsFromTabulator?.sorters ?? getTable?.()?.getSorters?.() ?? [];
  const mapped = mapQlty029SortersToQuery(rawSorters);
  if (mapped.length) extra.sorters = JSON.stringify(mapped);

  const hf = getTable?.()?.getHeaderFilters?.() ?? [];
  Object.assign(extra, mapQlty029HeaderFiltersToQuery(hf));

  const search = String(toolbarSearch ?? '').trim();
  if (search !== '') extra.toolbar_search = search;
  return extra;
}

/** Same-client emit payload for qlty028 / qlty030 sync bus. */
export function buildQlty029MutationDetail({
  kind,
  companyId,
  certificateId,
  recordId,
  changedFields,
} = {}) {
  const detail = { kind };
  if (companyId != null && companyId !== '') {
    detail.companyId = companyId;
    detail.company_id = companyId;
  }
  if (certificateId != null && certificateId !== '') detail.certificateId = certificateId;
  if (recordId != null && recordId !== '') detail.recordId = recordId;
  if (Array.isArray(changedFields) && changedFields.length) detail.changedFields = changedFields;
  return detail;
}

/** Create / delete / activate / deactivate → invalidate progressive session. */
export function isQlty029SessionInvalidatingKind(kind) {
  return (
    kind === OTK_COMPANY_CERT_KIND.PARENT_CREATED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_DELETED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_ACTIVATED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_DEACTIVATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_CREATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_DELETED
  );
}

/**
 * Safe in-place update only when the parent certificate row is already loaded.
 * Missing/unloaded rows must not use addData on a remote progressive session.
 */
export function shouldQlty029ApplySafeParentUpdate(kind, rowLoaded) {
  if (!rowLoaded) return false;
  return (
    kind === OTK_COMPANY_CERT_KIND.PARENT_UPDATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_UPDATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_FILE_CHANGED
  );
}
